import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import QuickAnalysisTab from '../../components/AI/QuickAnalysisTab';
import KitSuggestionsTab from '../../components/AI/KitSuggestionsTab';
import GroupVariationsTab from '../../components/AI/GroupVariationsTab';
import OpportunitiesTab from '../../components/AI/OpportunitiesTab';
import PersonalizedTipsTab from '../../components/AI/PersonalizedTipsTab';
import { productService } from '../../services/productService';

const AIInsights = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('variations');
  const [stats, setStats] = useState({
    productsAnalyzed: 0,
    variationsDetected: 0,
    opportunitiesFound: 0
  });

  const tabs = [
    { id: 'analysis', label: 'Análise Rápida', icon: 'Zap' },
    { id: 'kits', label: 'Sugestões de Kits', icon: 'Package' },
    { id: 'variations', label: 'Agrupar Variações', icon: 'Grid' },
    { id: 'opportunities', label: 'Oportunidades', icon: 'Lightbulb' },
    { id: 'tips', label: 'Dicas', icon: 'Sparkles' }
  ];

  useEffect(() => {
    loadRealStatistics();
  }, []);

  const loadRealStatistics = async () => {
    try {
      setLoading(true);
      const { products, total } = await productService?.getAll();
      
      // Calculate real statistics from CSV data
      const productsAnalyzed = total || 0;
      
      // Count variations detected (products with same sku_base)
      const skuBaseGroups = {};
      products?.forEach(p => {
        const base = p?.skuBase || p?.sku;
        if (base) {
          skuBaseGroups[base] = (skuBaseGroups?.[base] || 0) + 1;
        }
      });
      const variationsDetected = Object.values(skuBaseGroups)?.filter(count => count > 1)?.reduce((sum, count) => sum + count, 0);
      
      // Count opportunities (products with issues)
      const opportunities = [
        products?.filter(p => !p?.description || p?.description?.length < 50)?.length, // No description
        products?.filter(p => !p?.images || p?.images?.length < 2)?.length, // Few images
        products?.filter(p => p?.stock > 0 && p?.stock < 10)?.length, // Low stock
        products?.filter(p => !p?.shopeeReady && !p?.mercadolivreReady)?.length // Not published
      ];
      const opportunitiesFound = opportunities?.reduce((sum, count) => sum + count, 0);
      
      setStats({
        productsAnalyzed,
        variationsDetected,
        opportunitiesFound
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Header />
      <div className="container mx-auto px-6 py-6 lg:px-10 lg:py-8 max-w-7xl">
        {/* Breadcrumb */}
        <div className="mb-6">
          <BreadcrumbNavigation
            items={[
              { label: 'Início', path: '/' },
              { label: 'Ai-insights', path: '/ai-insights' }
            ]}
          />
        </div>

        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-purple-500 rounded-3xl shadow-2xl overflow-hidden mb-8">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-purple-400/10 rounded-full blur-3xl"></div>
          
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8 p-10 lg:p-12">
            {/* Left Side - AI Icon and Text */}
            <div className="flex items-center gap-6 flex-1">
              {/* AI Robot Icon in Rounded Square */}
              <div className="relative group">
                <div className="absolute inset-0 bg-white/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-6 shadow-2xl">
                  <svg className="w-16 h-16 text-blue-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C10.3431 2 9 3.34315 9 5V6H7C5.89543 6 5 6.89543 5 8V18C5 19.1046 5.89543 20 7 20H17C18.1046 20 19 19.1046 19 18V8C19 6.89543 18.1046 6 17 6H15V5C15 3.34315 13.6569 2 12 2Z" fill="currentColor" opacity="0.2"/>
                    <circle cx="9" cy="12" r="1.5" fill="currentColor"/>
                    <circle cx="15" cy="12" r="1.5" fill="currentColor"/>
                    <path d="M9 15H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="6" y="7" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>

              {/* Text Content */}
              <div className="text-white">
                <h1 className="text-4xl lg:text-5xl font-bold mb-3 drop-shadow-lg">
                  Assistente de Vendas IA
                </h1>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-md text-white rounded-full text-sm font-medium border border-white/30 shadow-lg">
                    <Icon name="Sparkles" size={14} className="text-blue-200" />
                    Powered by Google Gemini
                  </span>
                </div>
                <p className="text-lg text-white/90 font-light">
                  Análise inteligente do seu catálogo
                </p>
              </div>
            </div>

            {/* Right Side - Floating Stats Card with Real Data */}
            <div className="relative">
              <div className="absolute inset-0 bg-white/10 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 min-w-[320px] border border-white/20">
                {/* Stat 1 - Products Analyzed */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500/10 rounded-2xl flex items-center justify-center">
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats?.productsAnalyzed?.toLocaleString('pt-BR')}</div>
                    <div className="text-sm text-gray-500 font-medium">Produtos Analisados</div>
                  </div>
                </div>

                {/* Stat 2 - Variations Detected */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center">
                    <Icon name="Grid" size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats?.variationsDetected?.toLocaleString('pt-BR')}</div>
                    <div className="text-sm text-gray-500 font-medium">Variações Detectadas</div>
                  </div>
                </div>

                {/* Stat 3 - Opportunities Found */}
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-500/10 rounded-2xl flex items-center justify-center">
                    <Icon name="Lightbulb" size={24} className="text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats?.opportunitiesFound?.toLocaleString('pt-BR')}</div>
                    <div className="text-sm text-gray-500 font-medium">Oportunidades Encontradas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pill-Style Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 p-2">
          <div className="flex flex-wrap gap-2">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 min-w-[140px] px-6 py-4 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  activeTab === tab?.id
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30 scale-105'
                    : 'bg-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-lg p-8 lg:p-10 min-h-[600px]">
          {activeTab === 'analysis' && <QuickAnalysisTab />}
          {activeTab === 'kits' && <KitSuggestionsTab />}
          {activeTab === 'variations' && <GroupVariationsTab />}
          {activeTab === 'opportunities' && <OpportunitiesTab />}
          {activeTab === 'tips' && <PersonalizedTipsTab />}
        </div>
      </div>
    </div>
  );
};

export default AIInsights;